import React, { useMemo, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";
import { clearAuthUser, getAuthUser } from "../utils/auth";
import { apiFetch } from "../utils/apiClient";

const statusMap = {
  success: {
    heading: "Payment Successful",
    description: "Congratulations! Your payment has been processed successfully.",
    image: "/images/handi.png",
    // fallbackColor: "from-[#4ADE80] to-[#16A34A]",
  },
  failure: {
    heading: "Payment Unsuccessful",
    description: "Your payment could not be completed. Please try again.",
    image: "/images/payment-failure.png",
    fallbackColor: "from-[#FB7185] to-[#BE123C]",
  },
};

export default function PaymentResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const statusKey = location.state?.status;
  const packageTitle = location.state?.package?.title;
  const authUser = useMemo(() => getAuthUser(), []);
  const [processingPayPal, setProcessingPayPal] = useState(false);
  const [paypalError, setPaypalError] = useState("");
  const [paypalStatus, setPaypalStatus] = useState(null);

  // Handle PayPal redirect (when user returns from PayPal after payment)
  useEffect(() => {
    const provider = searchParams.get("provider");
    const token = searchParams.get("token"); // PayPal order ID

    if (provider === "paypal" && token && !statusKey && !processingPayPal && !paypalStatus) {
      setProcessingPayPal(true);
      setPaypalError("");

      // Capture the PayPal order - this completes the payment
      apiFetch("/payments/paypal/capture-order", {
        method: "POST",
        data: { orderId: token },
      })
        .then((response) => {
          if (response.success) {
            setPaypalStatus("success");
            setProcessingPayPal(false);
            // Clean URL and show success
            navigate("/payment-status", { 
              replace: true, 
              state: { 
                status: "success",
                method: "paypal",
                transactionId: response.transactionId,
              } 
            });
          } else {
            setPaypalError("Payment capture failed. Please contact support.");
            setProcessingPayPal(false);
            setPaypalStatus("failure");
          }
        })
        .catch((error) => {
          console.error("PayPal capture error:", error);
          setPaypalError(error?.data?.message || error.message || "Payment processing failed.");
          setProcessingPayPal(false);
          setPaypalStatus("failure");
        });
    }
  }, [searchParams, statusKey, navigate, processingPayPal, paypalStatus]);

  const finalStatusKey = paypalStatus || statusKey;
  const content = statusMap[finalStatusKey] ?? null;

  const handleBack = () => {
    navigate("/dashboard", { replace: true });
  };

  const handleSignOut = () => {
    clearAuthUser();
    navigate("/login", { replace: true });
  };

  const ResultShell = ({
    breadcrumb = ["Dashboard", "Packages", "Card or Bank Account", "Payment Verification"],
    title,
    subtitle,
    onBack,
    children,
  }) => (
    <div className="relative min-h-[calc(100vh-140px)] overflow-hidden rounded-3xl px-6 py-12 text-white sm:px-10 lg:px-12">
      <div className="pointer-events-none absolute inset-0">
        {/* <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_0%_100%,rgba(126,85,255,0.25),transparent)]" /> */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-[#1b0c38]/60 opacity-40" />
      </div>
      <div className="relative z-10 mx-auto flex w-full max-w-5xl flex-col gap-12">
        <header className="flex flex-col gap-6 text-left md:flex-row md:items-start md:justify-between">
          <div>
            <nav className="text-sm text-white/45">
              {breadcrumb.map((segment, index) => (
                <span key={segment} className="inline-flex items-center">
                  <span>{segment}</span>
                  {index < breadcrumb.length - 1 && (
                    <span className="mx-2 text-white/25">›</span>
                  )}
                </span>
              ))}
            </nav>
            {title && (
              <h1 className="mt-4 text-3xl font-semibold sm:text-4xl">{title}</h1>
            )}
            {subtitle && (
              <p className="mt-2 max-w-xl text-sm text-white/70 sm:text-base">
                {subtitle}
              </p>
            )}
          </div>

          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="self-start rounded-xl border border-white/15 bg-white/5 px-5 py-2 text-sm font-semibold text-white/70 transition hover:border-white/30 hover:text-white"
            >
              Back
            </button>
          )}
        </header>

        <div className="flex flex-col items-center gap-6 text-center">
          {children}
        </div>
      </div>
    </div>
  );

  // Show processing state for PayPal redirect
  if (processingPayPal) {
    return (
      <DashboardLayout
        user={authUser}
        onSignOut={handleSignOut}
        activeKey="packages"
      >
        {() => (
          <ResultShell
            title="Processing Payment"
            subtitle="Please wait while we finalize your payment..."
            onBack={() => navigate(-1)}
          >
            <div className="flex flex-col items-center gap-4">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-white/20 border-t-white"></div>
              {paypalError && (
                <p className="text-sm text-red-400">{paypalError}</p>
              )}
            </div>
          </ResultShell>
        )}
      </DashboardLayout>
    );
  }

  if (!content) {
    return (
      <DashboardLayout
        user={authUser}
        onSignOut={handleSignOut}
        activeKey="packages"
      >
        {() => (
          <ResultShell
            title="Payment status unavailable"
            subtitle="We couldn't determine the payment result. Please start the purchase flow again from the packages section."
            onBack={() => navigate(-1)}
          >
            {paypalError && (
              <p className="mb-4 text-sm text-red-400">{paypalError}</p>
            )}
            <button
              type="button"
              onClick={handleBack}
              className="rounded-full bg-[#7C42FF] px-8 py-3 text-sm font-semibold text-white transition hover:bg-[#925CFF]"
            >
              Back to Homepage
            </button>
          </ResultShell>
        )}
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      user={authUser}
      onSignOut={handleSignOut}
      activeKey="packages"
    >
      {() => (
        <ResultShell
          title="Payment Verification"
          subtitle="Your payment details are being finalized. Save this confirmation for your records and return to the dashboard to view updated balances and rewards."
          onBack={() => navigate(-1)}
        >
          <div className="relative mt-8 flex h-56 w-56 items-center justify-center sm:mt-12">
            <img
              src={content.image}
              alt={content.heading}
              className="h-full w-full"
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/10 via-transparent to-transparent" />
          </div>

          {packageTitle && (
            <p className="mt-8 text-xs uppercase tracking-[0.3em] text-white/40">
              {packageTitle}
            </p>
          )}

          <h2 className="text-xl font-semibold text-white">{content.heading}</h2>
          <p className="max-w-md text-sm text-white/70">{content.description}</p>

          <button
            type="button"
            onClick={handleBack}
            className="mt-6 rounded-full bg-[#7C42FF] px-8 py-3 text-sm font-semibold text-white transition hover:bg-[#9158FF]"
          >
            Back to Dashboard
          </button>
        </ResultShell>
      )}
    </DashboardLayout>
  );
}
